import { useEffect, useRef } from 'react'
import { getProfile, useInvalidateProfile } from '@entities/user'
import { useAppStore } from '@/store/useAppStore'

const FAST_POLL_MS = 1_000
const SLOW_POLL_MS = 5_000
const FAST_POLL_WINDOW_MS = 60_000

/**
 * Поллит /user/profile с частотой 1с первую минуту, затем 5с.
 * Как только `profile.telegram != null` — вызывает onLinked и останавливается.
 * @param active - Включён ли поллинг (обычно true пока открыта модалка ожидания)
 * @param onLinked - Колбэк при подтверждённой связке
 */
export const useTelegramLinkPolling = (active: boolean, onLinked: () => void | Promise<void>) => {
  const setProfile = useAppStore((s) => s.setProfile)
  const invalidateProfile = useInvalidateProfile()
  const timeoutRef = useRef<number | null>(null)
  const startedAtRef = useRef<number>(0)
  const stoppedRef = useRef(false)

  useEffect(() => {
    if (!active) return
    stoppedRef.current = false
    startedAtRef.current = Date.now()

    const tick = async () => {
      if (stoppedRef.current) return
      try {
        const profile = await getProfile()
        setProfile(profile)
        if (profile.telegram) {
          stoppedRef.current = true
          await invalidateProfile()
          await onLinked()
          return
        }
      } catch {
        // Продолжаем поллинг при сетевых ошибках.
      }
      const elapsed = Date.now() - startedAtRef.current
      const delay = elapsed < FAST_POLL_WINDOW_MS ? FAST_POLL_MS : SLOW_POLL_MS
      timeoutRef.current = window.setTimeout(tick, delay)
    }

    timeoutRef.current = window.setTimeout(tick, FAST_POLL_MS)
    return () => {
      stoppedRef.current = true
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])
}
